import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { AlertCircle, CheckCircle2, TrendingUp } from "lucide-react";

import { useDelayedFlag, useTran } from "@/hooks";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { useEsgDashboardStore } from "../../hooks";

const CategoryProgress = observer(() => {
  const { t } = useTran();
  const { categoryProgress, loading, ensureData } = useEsgDashboardStore();
  const showSkeleton = useDelayedFlag(loading);

  useEffect(() => {
    ensureData();
  }, [ensureData]);

  if (showSkeleton) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={`category-skeleton-${index}`}>
            <CardContent className="space-y-4 p-5">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-8 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!categoryProgress.length) {
    return (
      <div className="rounded-radius border border-dashed border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        {t("No category data available.")}
      </div>
    );
  }

  const resolveColor = (percentage) => {
    if (percentage >= 80) return "text-emerald-500";
    if (percentage >= 50) return "text-amber-500";
    return "text-rose-500";
  };

  const resolveBar = (percentage) => {
    if (percentage >= 80) return "bg-emerald-500";
    if (percentage >= 50) return "bg-amber-500";
    return "bg-rose-500";
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {categoryProgress.map((category) => (
        <Card
          key={category.category}
          className="border border-border/80 shadow-sm"
        >
          <CardContent className="p-5">
            <div className="mb-4 flex items-center justify-between">
              <p className="font-semibold text-foreground">
                {category.category}
              </p>
              <span
                className={`text-2xl font-bold ${resolveColor(category.percentage)}`}
              >
                {category.percentage}%
              </span>
            </div>
            <div className="mb-4 h-2 rounded-full bg-muted">
              <div
                className={`h-2 rounded-full ${resolveBar(category.percentage)}`}
                style={{ width: `${category.percentage}%` }}
              />
            </div>

            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-radius bg-emerald-500/10 p-2">
                <div className="flex items-center justify-center gap-1 text-emerald-500">
                  <CheckCircle2 className="h-3 w-3" />
                  <span className="font-bold">{category.onTrack}</span>
                </div>
                <p className="text-muted-foreground">{t("On Track")}</p>
              </div>
              <div className="rounded-radius bg-amber-500/10 p-2">
                <div className="flex items-center justify-center gap-1 text-amber-500">
                  <AlertCircle className="h-3 w-3" />
                  <span className="font-bold">{category.atRisk}</span>
                </div>
                <p className="text-muted-foreground">{t("At Risk")}</p>
              </div>
              <div className="rounded-radius bg-blue-500/10 p-2">
                <div className="flex items-center justify-center gap-1 text-blue-500">
                  <TrendingUp className="h-3 w-3" />
                  <span className="font-bold">{category.overTarget}</span>
                </div>
                <p className="text-muted-foreground">{t("Over Target")}</p>
              </div>
            </div>

            <p className="mt-4 text-center text-xs text-muted-foreground">
              {category.completed} {t("of")} {category.total} {t("Complete")}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
});

export default CategoryProgress;
